import React from 'react';
import { Send, Phone, Shield } from 'lucide-react';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { trackContactFormConversion } from '../lib/analytics';
import { TrackedPhoneLink } from './ui/TrackedPhoneLink';

const inlineFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  phone: z.string().min(1, "Phone is required").max(20),
  serviceNeeded: z.string().min(1, "Please select a service"),
  website: z.string().max(0).default(""),
  company: z.string().max(0).default(""),
  formStartTime: z.number().min(0)
});

type InlineForm = z.infer<typeof inlineFormSchema>;

interface InlineQuoteFormProps {
  source: string;
  defaultService?: string;
  headline?: string;
  subheadline?: string;
  variant?: 'dark' | 'light';
}

const serviceOptions = [
  "Network Cabling",
  "IT Support",
  "Security Cameras",
  "UniFi Installation",
  "Network Setup",
  "Access Control",
  "VoIP Phone Systems",
  "Other"
];

export function InlineQuoteForm({
  source,
  defaultService = "",
  headline = "Tell Us About Your Project",
  subheadline,
  variant = 'dark'
}: InlineQuoteFormProps) {
  const [formState, setFormState] = React.useState<InlineForm>({
    name: '',
    phone: '',
    serviceNeeded: defaultService,
    website: '',
    company: '',
    formStartTime: Date.now()
  });

  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errors, setErrors] = React.useState<Partial<Record<keyof InlineForm, string>>>({});
  const [lastSubmissionTime, setLastSubmissionTime] = React.useState<number>(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const now = Date.now();
    if (now - lastSubmissionTime < 30000) {
      toast.error('Please wait before submitting again.');
      return;
    }

    setStatus('submitting');
    setErrors({});

    try {
      const validatedData = inlineFormSchema.parse(formState);

      const timeTaken = now - validatedData.formStartTime;
      if (timeTaken < 3000) {
        toast.error('Please take your time filling out the form.');
        setStatus('error');
        return;
      }

      const suspiciousPatterns = [/^[a-zA-Z]{10,}$/, /\d{10,}/, /(.)\1{5,}/];
      const allFields = `${validatedData.name} ${validatedData.phone}`;
      if (suspiciousPatterns.some(pattern => pattern.test(allFields))) {
        toast.error('Invalid form data detected.');
        setStatus('error');
        return;
      }

      const leadData = {
        form_name: 'inline-quote-form',
        name: validatedData.name,
        phone: validatedData.phone,
        message: `Quick quote request from ${source}`,
        service_interest: validatedData.serviceNeeded,
        source,
        page_url: window.location.href,
      };

      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadData)
      });

      if (response.ok) {
        // Count the lead only once the submission actually succeeded
        trackContactFormConversion(validatedData.serviceNeeded, 'inline_quote');
        setStatus('success');
        setLastSubmissionTime(now);
        toast.success("Request received! We'll call you back shortly.");
        setFormState({
          name: '',
          phone: '',
          serviceNeeded: defaultService,
          website: '',
          company: '',
          formStartTime: Date.now()
        });
      } else {
        throw new Error(`Submission failed: ${response.status}`);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof InlineForm, string>> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            fieldErrors[err.path[0] as keyof InlineForm] = err.message;
          }
        });
        setErrors(fieldErrors);
        toast.error('Please check the form and correct any errors.');
      } else {
        toast.error('Something went wrong. Please call us at (208) 328-4500.');
      }
      setStatus('error');
    }
  };

  const isDark = variant === 'dark';

  if (status === 'success') {
    return (
      <div className={`rounded-2xl p-8 text-center ${isDark ? 'bg-green-900/20 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
        <div className={`text-5xl mb-4 ${isDark ? 'text-green-400' : 'text-green-600'}`}>&#10003;</div>
        <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Request Received!</h3>
        <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          We'll call you back soon. If you'd rather talk now:
        </p>
        <TrackedPhoneLink
          location={`inline_form_success_${source}`}
          className={`inline-flex items-center gap-2 font-bold text-lg transition-colors ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-500'}`}
        >
          <Phone className="w-5 h-5" />
          (208) 328-4500
        </TrackedPhoneLink>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-8 md:p-10 ${isDark ? 'bg-gradient-to-br from-cobalt-900/60 to-navy-900/80 border border-cobalt-500/20' : 'bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200'}`}>
      <div className="max-w-2xl mx-auto text-center">
        <div className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-4 ${isDark ? 'bg-green-500/10 text-green-400' : 'bg-green-100 text-green-700'}`}>
          <Shield className="w-4 h-4" />
          <span className="text-xs font-semibold">COMMERCIAL PROJECTS • NO OBLIGATION</span>
        </div>
        <h3 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {headline}
        </h3>
        {subheadline && (
          <p className={`text-lg md:text-xl font-semibold mb-2 ${isDark ? 'text-cobalt-300' : 'text-primary-700'}`}>
            {subheadline}
          </p>
        )}
        <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Tell us what you need and we'll call you back.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Honeypot */}
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={formState.website} onChange={(e) => setFormState(prev => ({ ...prev, website: e.target.value }))} />
            <input type="text" name="company" tabIndex={-1} autoComplete="off" value={formState.company} onChange={(e) => setFormState(prev => ({ ...prev, company: e.target.value }))} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <input
                type="text"
                placeholder="Your Name *"
                value={formState.name}
                onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-4 py-3 min-h-[48px] text-base rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${isDark ? 'bg-white/10 border border-white/20 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500'}`}
              />
              {errors.name && <p className="text-red-400 text-xs mt-1 text-left">{errors.name}</p>}
            </div>
            <div>
              <input
                type="tel"
                placeholder="Phone Number *"
                value={formState.phone}
                onChange={(e) => setFormState(prev => ({ ...prev, phone: e.target.value }))}
                className={`w-full px-4 py-3 min-h-[48px] text-base rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${isDark ? 'bg-white/10 border border-white/20 text-white placeholder-gray-400' : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500'}`}
              />
              {errors.phone && <p className="text-red-400 text-xs mt-1 text-left">{errors.phone}</p>}
            </div>
            <div>
              <select
                value={formState.serviceNeeded}
                onChange={(e) => setFormState(prev => ({ ...prev, serviceNeeded: e.target.value }))}
                className={`w-full px-4 py-3 min-h-[48px] text-base rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all appearance-none ${isDark ? 'bg-white/10 border border-white/20 text-white' : 'bg-white border border-gray-300 text-gray-900'}`}
                style={isDark ? { colorScheme: 'dark' } : undefined}
              >
                <option value="" className={isDark ? 'bg-navy-900 text-gray-400' : ''}>Service Needed *</option>
                {serviceOptions.map(option => (
                  <option key={option} value={option} className={isDark ? 'bg-navy-900 text-white' : ''}>{option}</option>
                ))}
              </select>
              {errors.serviceNeeded && <p className="text-red-400 text-xs mt-1 text-left">{errors.serviceNeeded}</p>}
            </div>
          </div>

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full sm:w-auto px-8 py-3 min-h-[48px] bg-green-600 hover:bg-green-500 disabled:bg-green-800 disabled:cursor-not-allowed text-white font-bold text-base rounded-xl transition-all duration-300 shadow-lg shadow-green-900/30 hover:shadow-green-900/50 inline-flex items-center justify-center gap-2"
          >
            {status === 'submitting' ? 'Sending...' : (
              <>
                <Send className="w-4 h-4" />
                Request a Quote
              </>
            )}
          </button>
        </form>

        <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Or call us directly:{' '}
          <TrackedPhoneLink location={`inline_form_${source}`} className={`font-medium transition-colors ${isDark ? 'text-white hover:text-green-400' : 'text-primary-600 hover:text-primary-700'}`}>
            (208) 328-4500
          </TrackedPhoneLink>
        </p>
      </div>
    </div>
  );
}
